/**
 * Money-amount helpers for the config-settings admin UIs (smr-admin product
 * settings and per-dealer settings).
 *
 * The backend (Brick\Money via common-package's `MoneyResource`) is the
 * canonical authority on currency math. The frontend only needs to ship
 * the user's typed amount in the `MoneyJson` shape the API accepts.
 *
 * We deliberately avoid `parseFloat(amount) * 100` because IEEE-754
 * binary floats cannot exactly represent most decimal fractions
 * (e.g. `0.1 + 0.2 !== 0.3`), so multiplying by 100 can produce a
 * `minor` value that does not match the displayed `amount`. The helpers
 * below operate on the canonical 2dp string representation instead.
 */

/**
 * Normalize a user-typed money string into the canonical 2dp string used by
 * MoneyResource: e.g. "" → null, "50" → "50.00", "50.1" → "50.10",
 * "50.123" → "50.12" (truncated, not rounded — server will canonicalize),
 * "abc" → null.
 */
export function normalizeAmountString(input: string): string | null {
    const trimmed = input.trim();
    if (trimmed === '') return null;
    // Allow optional sign, digits, single dot, more digits.
    const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(trimmed);
    if (!match) return null;
    const sign = match[1] ?? '';
    const whole = match[2] ?? '0';
    let frac = match[3] ?? '';
    if (frac.length === 0) frac = '00';
    else if (frac.length === 1) frac = `${frac}0`;
    else if (frac.length > 2) frac = frac.slice(0, 2);
    return `${sign}${whole}.${frac}`;
}

/**
 * Compute integer minor units from a canonical 2dp amount string using
 * only string/integer arithmetic — no floating point.
 * Returns null if the input cannot be parsed.
 */
export function minorUnitsFromAmount(amount: string): number | null {
    const normalized = normalizeAmountString(amount);
    if (normalized === null) return null;
    const [signedWhole, frac] = normalized.split('.');
    const negative = signedWhole.startsWith('-');
    const whole = negative ? signedWhole.slice(1) : signedWhole;
    const wholeInt = parseInt(whole, 10);
    const fracInt = parseInt(frac, 10);
    if (!Number.isFinite(wholeInt) || !Number.isFinite(fracInt)) return null;
    const minor = wholeInt * 100 + fracInt;
    return negative ? -minor : minor;
}
